import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../../utils/axios';
import { useFormValidation } from '../../utils/validation';
import './auth.css';

const InvitationValidationPage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invitationId, setInvitationId] = useState('');
  const navigate = useNavigate();

  const {
    fieldErrors,
    fieldValid,
    handleBlur: handleValidationBlur,
    handleFieldChange,
    validateAllFields,
    getFieldClass
  } = useFormValidation(['email']);

  useEffect(() => {
    const invitationIdParam = searchParams.get('invitation');
    if (invitationIdParam) {
      setInvitationId(invitationIdParam);
    } else {
      setError('No invitation link provided');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate form before submission
    if (!validateAllFields({ email })) {
      setLoading(false);
      setError('Please fix the errors above before submitting');
      return;
    }

    try {
      const response = await apiClient.post('/iam/validate-invitation', {
        invitation_id: invitationId,
        email: email.trim().toLowerCase()
      });

      const data = response.data;

      if (data.valid) {
        // Navigate to signup page with invitation data in state
        const ssoFlag = searchParams.get('sso') === 'true';
        navigate('/signup', {
          state: {
            validatedInvitation: {
              invitation_id: invitationId,
              email: email.trim().toLowerCase(),
              tenant_id: data.tenant_info.tenant_id,
              tenant_code: data.tenant_info.tenant_code,
              tenant_name: data.tenant_info.tenant_name,
              role: data.invitation_details.role,
              sso: ssoFlag,
            }
          }
        });
      } else {
        setError(data.message || 'Invitation validation failed');
      }
    } catch (err) {
      if (err.response && err.response.data) {
        setError(err.response.data.message || 'Validation failed');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!invitationId) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Invalid Invitation</h1>
          </div>
          <div className="error-message">
            No invitation link provided. Please check your invitation link and try again.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Join TouchFlow</h1>
          <p>Please verify your email address to proceed with the invitation</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className={`form-group ${getFieldClass('email')}`}>
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                handleFieldChange('email', e.target.value, { email: e.target.value });
              }}
              onBlur={(e) => handleValidationBlur('email', e.target.value, { email: e.target.value })}
              placeholder="Enter the email address this invitation was sent to"
              required
              disabled={loading}
            />
            <div className="validation-message">
              {fieldErrors.email && (
                <span className="field-error">{fieldErrors.email}</span>
              )}
              {fieldValid.email && !fieldErrors.email && (
                <span className="field-success">✓ Valid email address</span>
              )}
            </div>
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Validating...' : 'Verify Email'}
          </button>
        </form>

        <div className="auth-footer">
          <p>Having trouble? Contact your administrator for assistance.</p>
        </div>
      </div>
    </div>
  );
};

export default InvitationValidationPage; 